import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import cx from 'classnames';
import { AppContext } from '../app/AppProvider.component';
import {
  getExternalAppRedirectUrlAction,
  fetchSherpathAIContextAction,
  trackAdobeAction,
  trackNewRelicAction
} from '../app/app-state-actions.utilities';
import {
  getIsAIChatSidebarEnabled,
} from '../app/app-state-selectors.utilities';
import {
  AppAction,
  Application
} from '../../apis/eols-app-link/eols-app-link.constants';
import { getAssessmentIdForInit } from '../../utilities/remediation.utilities';
import { FlexLayout } from '../flex/FlexLayout.component';
import { FlexItem } from '../flex/FlexItem.component';
import { FlexLayoutModifier } from '../flex/flex.constants';
import { ELSIcon } from '../els.components';
import { RemediationAssignmentWithSourcesDto } from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import {
  AdobeAnalyticsAction,
  AdobeAnalyticsButtonType
} from '../../models/adobe-analytics.models';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import { SherpathAIContextType, SherpathAIIframeMessageType } from './sherpath-ai-chat-sidebar.constants';
import { getSherpathAIOrigin, isAllowedMessageOrigin } from './sherpath-ai-chat-sidebar.utilities';
import { PageContextType } from '../../utilities/sherpath-ai-context.utilities';
import { getProduct, getProductConfig } from '../../utilities/product.utilities';
import { Product } from '../../constants/product.constants';

export type SherpathAIChatSidebarProps = {
  remediationAssignmentWithSources: RemediationAssignmentWithSourcesDto;
  pageContext: string;
  pageContextType: PageContextType;
};

export const SherpathAIChatSidebar = (props: SherpathAIChatSidebarProps): JSX.Element => {
  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);
  const [chatIframeUrl, setChatIframeUrl] = useState<string>(null);
  const [isSherpathAIIframeReady, setIsSherpathAIIframeReady] = useState<boolean>(false);

  const {
    remediationAssignmentWithSources,
    pageContext,
    pageContextType
  } = props;

  const isAIChatSidebarEnabled = getIsAIChatSidebarEnabled(appState, remediationAssignmentWithSources);
  const { aiChatName } = getProductConfig(getProduct(remediationAssignmentWithSources));

  // Listen for Sherpath AI iframe ready message (handshake)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate origin for security - only accept messages from trusted sources
      if (!isAllowedMessageOrigin(event.origin)) {
        return;
      }

      if (event.data?.type === SherpathAIIframeMessageType.SHERPATH_AI_IFRAME_READY) {
        setIsSherpathAIIframeReady(true);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Post page context to iframe when iframe is ready and data is available
  useEffect(() => {
    if (!isAIChatSidebarEnabled || !isSherpathAIIframeReady) {
      return;
    }

    if (pageContext === null) {
      return;
    }

    // Post page context to iframe via postMessage
    const iframe = document.querySelector('.c-sherpath-ai-chat-sidebar__iframe') as HTMLIFrameElement;

    if (!iframe?.contentWindow) {
      return;
    }

    const message = {
      type: SherpathAIIframeMessageType.SHERPATH_AI_PAGE_CONTEXT_UPDATE,
      pageContext
    };
    iframe.contentWindow.postMessage(message, getSherpathAIOrigin());
  }, [isAIChatSidebarEnabled, isSherpathAIIframeReady, pageContext]);

  const getChatUrl = () => {

    if (!remediationAssignmentWithSources) {
      return Promise.resolve(null);
    }

    const contextId = getAssessmentIdForInit(appState)?.toString() || '';

    // Spark uses its own NACS entry context, which has no programType/taxons, so /sherpath-ai-context is not needed.
    // `program` is omitted on purpose: NACS only accepts it with bookPlatform CKS (400 otherwise), and VST always uses the general medical book bank.
    const contextPromise: Promise<Record<string, unknown>> = getProduct(remediationAssignmentWithSources) === Product.SPARK
      ? Promise.resolve({
        contextType: SherpathAIContextType.SPARK,
        contextId,
        pageContext,
        isTutorModeEnabled: true,
        bookPlatform: 'VST'
      })
      : fetchSherpathAIContextAction({
        appState,
        appStateRef,
        setAppState,
        plpAssignmentId: remediationAssignmentWithSources.remediationAssignment?.id.toString(),
        useCache: true
      }).then((plpContext) => plpContext && {
        contextType: SherpathAIContextType.PLP,
        contextId,
        programType: plpContext.programType,
        taxons: plpContext.taxons,
        pageContext
      });

    return contextPromise.then((context) => {
      if (!context) {
        return null;
      }

      // Fetch the app link URL for the chat iframe
      return getExternalAppRedirectUrlAction({
        appState,
        appStateRef,
        setAppState,
        config: {
          action: AppAction.LAUNCH,
          app: Application.NHE_AI,
          body: {
            isIframe: true,
            hideNav: false,
            enableHeaderLogoIframeLink: false,
            disabledMyBooks: true,
            context,
            instanceId: contextId,
          },
          includeLinkHash: true,
          altSrcApp: Application.REMEDIATION
        }
      })
    });
  };

  useEffect(() => {
    if (!isAIChatSidebarEnabled) {
      return;
    }
    if (chatIframeUrl) {
      return;
    }

    if (!appState.isChatActive) {
      return;
    }

    getChatUrl().then((response) => {
      if (!response) {
        return;
      }
      setChatIframeUrl(response.redirectUrl);
    });
  }, [isAIChatSidebarEnabled, remediationAssignmentWithSources, appState.isChatActive]);

  const handleChatClose = () => {
    trackAdobeAction({
      appStateRef,
      actionProps: {
        action: AdobeAnalyticsAction.BUTTON_CLICK,
        pageData: {
          buttonType: `${pageContextType}:${AdobeAnalyticsButtonType.BUTTON}:sherpathAiClose`,
        }
      }
    });

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.SHERPATH_AI_TOGGLED,
        props: {
          targetState: 'closed',
          pageContextType,
          remediationAssignmentId: remediationAssignmentWithSources?.remediationAssignment?.id
        }
      }
    });

    setAppState((prevState) => {
      return {
        ...prevState,
        isChatActive: false
      };
    });
  };

  const handleOpenInNewTab = () => {
    trackAdobeAction({
      appStateRef,
      actionProps: {
        action: AdobeAnalyticsAction.BUTTON_CLICK,
        pageData: {
          buttonType: `${pageContextType}:${AdobeAnalyticsButtonType.BUTTON}:sherpathAiOpenInNewTab`,
        }
      }
    });

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.SHERPATH_AI_OPEN_IN_NEW_TAB_CLICKED,
        props: {
          pageContextType,
          remediationAssignmentId: remediationAssignmentWithSources?.remediationAssignment?.id
        }
      }
    });

    getChatUrl().then((response) => {
      if (!response) {
        return;
      }
      window.open(response.redirectUrl, '_blank');
    });
  };

  if (!isAIChatSidebarEnabled) {
    return null;
  }

  return (
    <div
      className={cx('c-sherpath-ai-chat-sidebar', {
        'c-sherpath-ai-chat-sidebar--active': appState.isChatActive,
      })}
    >
      <div className="c-sherpath-ai-chat-sidebar__header">
        <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.GUTTERS]}>
          <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
            <FlexLayout modifiers={[
              FlexLayoutModifier.LEFT,
              FlexLayoutModifier.MIDDLE,
              FlexLayoutModifier.GUTTERS,
              FlexLayoutModifier.WRAP_AT_DESKTOP
            ]}>
              <FlexItem
                modifiers={[FlexLayoutModifier.GROW]}
                classes={['u-els-margin-bottom-1o2@desktop u-els-width-1o1@desktop']}
              >
                <h4>{aiChatName}</h4>
              </FlexItem>
              <FlexItem
                classes={['u-els-width-1o1@desktop']}
              >
                <button
                  type="button"
                  className="u-els-anchorize u-els-debuttonize u-els-display-block"
                  onClick={handleOpenInNewTab}
                >
                  Open in new tab
                </button>
              </FlexItem>
            </FlexLayout>
          </FlexItem>
          <FlexItem>
            <button
              type="button"
              className="u-els-anchorize u-els-debuttonize u-els-display-block"
              onClick={handleChatClose}
            >
              <ELSIcon name="close" size="1x"
                       customClass="u-els-display-block"
              />
              <span className="u-els-hide-visually">Close</span>
            </button>
          </FlexItem>
        </FlexLayout>
      </div>
      {chatIframeUrl && (
        <iframe
          src={chatIframeUrl}
          title="AI Chat"
          className="c-sherpath-ai-chat-sidebar__iframe"
          allowFullScreen
          allow="clipboard-read; clipboard-write"
        />
      )}
    </div>
  );
};
